/* =============================================================================
   assets/rm-local.js
   Geolocation-based local store detection.
   Uses IPStack for IP-based coordinates, falls back to browser geolocation.
   Matched store is cached in sessionStorage for 6 hours.
   ============================================================================= */

(function () {
  'use strict';

  /* ── Config ── */
  const IPSTACK_KEY    = 'ad0b6e301c14eabc36327819a8ec8557';
  const SESSION_KEY    = 'rm_local_v2';
  const stores         = window.RMStoreLocations || [];
  const headerSettings = window.RMHeaderSettings || {};
  const radiusMiles    = headerSettings.radiusMiles || 40;

  if (!stores.length) return;

  if (headerSettings.localEnabled === false) {
    try { sessionStorage.removeItem(SESSION_KEY); } catch (_) {}
    return;
  }


  /* ── Haversine distance (miles) ── */
  function distanceMiles(lat1, lon1, lat2, lon2) {
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) ** 2;
    return 3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /* ── Find nearest store within radius ── */
  function findNearestStore(lat, lon) {
    let nearest  = null;
    let minDist  = Infinity;
    stores.forEach(store => {
      const dist = distanceMiles(lat, lon, store.latitude, store.longitude);
      if (dist < radiusMiles && dist < minDist) {
        nearest = store;
        minDist = dist;
      }
    });
    return nearest;
  }


  /* ── Apply store to header UI ── */
  function applyStoreToHeader(store) {
    const annSettings = window.RMAnnBarSettings || {};

    if (headerSettings.localEnabled !== false) {
      // Hide non-local elements
      document.querySelectorAll('.rh-nonlocal').forEach(el => el.style.display = 'none');

      // Show store button and name
      const storeBtnEl  = document.getElementById('rh-store-btn');
      const storeNameEl = document.getElementById('rh-store-name');
      storeBtnEl?.classList.add('is-visible');
      if (storeNameEl) storeNameEl.textContent = store.store_name;
      storeBtnEl?.setAttribute('aria-label', `${store.store_name} — store info`);

      // Populate header dropdown
      populateHeaderDropdown(store);

      // Animate store name greeting on scroll
      animateStoreGreeting(store.store_name);
    }

    if (annSettings.showLocation !== false) {
      // Show store in announcement bar
      const annWrapEl  = document.getElementById('ann-store-wrap');
      const annNameEl  = document.getElementById('announcement-local-name');
      if (annNameEl) annNameEl.textContent = store.store_name;
      if (annWrapEl) annWrapEl.style.display = 'block';
      populateAnnouncementDropdown(store);
    }

    // The announcement bar (and possibly the header) just changed size —
    // recalc the sticky header offset/spacer immediately rather than
    // waiting for the ResizeObserver in header.liquid to catch up on its
    // own next tick. Removes the visible "settle then shrink" once local-
    // store detection resolves (this cached path runs on every page nav,
    // not just first load, since sessionStorage makes it synchronous).
    if (typeof window.RMSyncHeaderOffset === 'function') {
      window.RMSyncHeaderOffset();
    }
  }

  /* ── Populate header store dropdown ── */
  function populateHeaderDropdown(store) {
    const nameEl    = document.getElementById('rh-sd-name');
    const addressEl = document.getElementById('rh-sd-address');
    const phoneEl   = document.getElementById('rh-sd-phone-link');
    const visitEl   = document.getElementById('rh-sd-visit');

    if (nameEl)    nameEl.textContent    = store.store_name;
    if (addressEl) addressEl.textContent = store.address
      ? `${store.address}, ${store.city}, ${store.state}` : '';

    if (phoneEl && store.phone_number) {
      phoneEl.href        = `tel:${store.phone_number.replace(/[^0-9+]/g, '')}`;
      phoneEl.textContent = store.phone_number;
    }

    renderHoursTable(store, 'rh-sd-hours');

    if (visitEl && store.link_to_visit_store) {
      visitEl.href = store.link_to_visit_store;
    }
  }

  /* ── Populate announcement bar store dropdown ── */
  function populateAnnouncementDropdown(store) {
    const nameEl    = document.getElementById('ann-sd-name');
    const addressEl = document.getElementById('ann-sd-address');
    const phoneEl   = document.getElementById('ann-sd-phone-link');
    const visitEl   = document.getElementById('ann-sd-visit');

    if (nameEl)    nameEl.textContent    = store.store_name;
    if (addressEl) addressEl.textContent = store.address
      ? `${store.address}, ${store.city}, ${store.state}` : '';

    if (phoneEl && store.phone_number) {
      phoneEl.href        = `tel:${store.phone_number.replace(/[^0-9+]/g, '')}`;
      phoneEl.textContent = store.phone_number;
    }

    renderHoursTable(store, 'ann-sd-hours');

    if (visitEl && store.link_to_visit_store) {
      visitEl.href = store.link_to_visit_store;
    }
  }

  /* ── Render hours table into a container ── */
  function renderHoursTable(store, containerId) {
    const container = document.getElementById(containerId);
    if (!container) return;

    const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const DAY_SHORT  = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    // Aliases that appear in the metaobject (lowercase)
    const DAY_ALIASES = {
      'sun': 0, 'sunday': 0,
      'mon': 1, 'monday': 1,
      'tue': 2, 'tues': 2, 'tuesday': 2,
      'wed': 3, 'weds': 3, 'wednesday': 3,
      'thu': 4, 'thurs': 4, 'thursday': 4,
      'fri': 5, 'friday': 5,
      'sat': 6, 'saturday': 6
    };

    // Build a per-day map from the metaobject store_hours string.
    // Format: one entry per line, e.g.:
    //   Mon & Sunday: Closed
    //   Tues: 10am-5pm
    //   Wed: 12pm-5pm
    const hoursMap = {}; // index 0-6 → hours string
    if (store.store_hours && typeof store.store_hours === 'string') {
      const lines = store.store_hours.split(/\n|,/).map(s => s.trim()).filter(Boolean);
      lines.forEach(line => {
        const colonIdx = line.indexOf(':');
        if (colonIdx === -1) return;
        const daysPart  = line.slice(0, colonIdx).trim();
        const hoursPart = line.slice(colonIdx + 1).trim();
        // Split day part on & or / or 'and'
        const dayTokens = daysPart.split(/\s*[&\/]\s*|\s+and\s+/i).map(d => d.trim().toLowerCase());
        dayTokens.forEach(token => {
          const idx = DAY_ALIASES[token];
          if (idx !== undefined) hoursMap[idx] = hoursPart;
        });
      });
    }

    function getHoursForDay(idx) {
      return hoursMap[idx] || 'Closed';
    }

    // Determine if currently open based on today's hours string.
    // Handles formats like: 10am-5pm, 10am–6pm, 9am-2pm, 12pm-5pm, Closed
    function parseOpenStatus(hoursStr) {
      if (!hoursStr || /closed/i.test(hoursStr)) return false;
      const now = new Date();
      const nowMins = now.getHours() * 60 + now.getMinutes();

      // Match patterns like "10am-5pm", "12pm-5pm", "9am–2pm"
      const m = hoursStr.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*[\-–]\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
      if (!m) return false;

      let openH = parseInt(m[1]), openM = parseInt(m[2] || 0), openAmPm = (m[3] || '').toLowerCase();
      let closeH = parseInt(m[4]), closeM = parseInt(m[5] || 0), closeAmPm = (m[6] || '').toLowerCase();

      // Resolve AM/PM. If only one suffix given, infer the other.
      if (!closeAmPm) closeAmPm = openAmPm;
      if (!openAmPm) openAmPm = closeAmPm;

      // Convert to 24h
      if (closeAmPm === 'pm' && closeH !== 12) closeH += 12;
      if (closeAmPm === 'am' && closeH === 12) closeH = 0;
      if (openAmPm === 'pm' && openH !== 12) openH += 12;
      if (openAmPm === 'am' && openH === 12) openH = 0;

      const openMins  = openH * 60 + openM;
      const closeMins = closeH * 60 + closeM;
      return nowMins >= openMins && nowMins < closeMins;
    }

    const today     = new Date();
    const todayIdx  = today.getDay();
    const todayHours = getHoursForDay(todayIdx);
    const isOpen    = parseOpenStatus(todayHours);

    // Build rows: Today first (highlighted), then the rest in Mon–Sun order
    // excluding today so it doesn't appear twice
    const orderedDays = [1, 2, 3, 4, 5, 6, 0]; // Mon through Sun

    let html = `
      <div class="rh-sd-row today">
        <span>Today</span>
        <span style="display:flex;align-items:center;gap:6px;">
          ${isOpen
            ? '<span style="width:6px;height:6px;border-radius:50%;background:var(--success,#27ae60);flex-shrink:0;"></span>'
            : ''}
          ${todayHours}
        </span>
      </div>`;

    orderedDays.forEach(idx => {
      if (idx === todayIdx) return; // skip today — already shown above
      html += `<div class="rh-sd-row"><span>${DAY_LABELS[idx]}</span><span>${getHoursForDay(idx)}</span></div>`;
    });

    container.innerHTML = html;
  }

  /* ── Animate store name greeting on scroll ── */
  function animateStoreGreeting(storeName) {
    const nameEl = document.getElementById('rh-store-name');
    if (!nameEl) return;
    nameEl.textContent = storeName;

    let greeted = false;
    window.addEventListener('scroll', function () {
      if (window.scrollY > 80 && !greeted) {
        greeted = true;
        nameEl.style.transition = 'opacity 0.25s';
        nameEl.style.opacity = '0';
        setTimeout(() => {
          nameEl.textContent = 'Hey Neighbor!';
          nameEl.style.opacity = '1';
        }, 250);
      } else if (window.scrollY <= 80 && greeted) {
        greeted = false;
        nameEl.style.opacity = '0';
        setTimeout(() => {
          nameEl.textContent = storeName;
          nameEl.style.opacity = '1';
        }, 250);
      }
    }, { passive: true });
  }


  /* ── Geolocation flow ── */
  function handleCoords(lat, lon) {
    const store = findNearestStore(lat, lon);
    saveToSession(store);
    if (store) applyStoreToHeader(store);
  }

  function saveToSession(store) {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ store: store || null, ts: Date.now() }));
    } catch (_) {}
  }

  function init() {
    // Check session cache first (valid for 6 hours)
    try {
      const cached = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
      if (cached && Date.now() - cached.ts < 6 * 60 * 60 * 1000) {
        if (cached.store) applyStoreToHeader(cached.store);
        return;
      }
    } catch (_) {}

    // Try IPStack for IP-based location
    fetch(`https://api.ipstack.com/check?access_key=${IPSTACK_KEY}`)
      .then(r => r.json())
      .then(data => {
        const { latitude, longitude } = data;
        if (!latitude || !longitude) throw new Error('No coords from IPStack');
        handleCoords(latitude, longitude);
      })
      .catch(() => {
        // Fall back to browser geolocation
        if (!navigator.geolocation) {
          saveToSession(null);
          return;
        }
        navigator.geolocation.getCurrentPosition(
          pos => handleCoords(pos.coords.latitude, pos.coords.longitude),
          ()  => saveToSession(null),
          { timeout: 5000, maximumAge: 30 * 60 * 1000 }
        );
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
